/**
 * @description This component will show existing notifications in a table
 */
/**
 * step 1 get all POIs in 
 * step 2 filter by group/user 
 * step 3 add check boxes to let user select which POIs to add notif
 * step 2 integrate with notification modal
 */

import { useEffect, useState, Fragment } from "react";
//components
import ModalWrapper from "../../../components/ui/modals/ModalWrapper";
import { NotificationDialog } from "./NotificationDialog";
import { RunSelect } from "./components/NotificationInputs";
import Checkbox from "../../../components/ui/checkbox/Checkbox";
import { Button } from "../../../components/ui/buttons/Button";
import MapIcon from "../../../assets/icons/google-maps-icon.png"
//api
import { getPOIWithNotificationByRunAPI } from "../../../services/poiAPI";
//hooks
import { useLoading } from "../../../contexts/LoadingContext";
import { useError } from "../../../contexts/ErrorContext";
import useRunOptions from "../../../hooks/useRunOptions";
import useConfirmDialog from "../../../hooks/useConfirmDialog";
import { useSuccess } from "../../../contexts/SuccessContext";
import usePoiGroupService from "../../../hooks/pois/usePoiGroupsService";
//context
import { useUser } from "../../../contexts/UserContext";
import { deletePOINotificationAPI } from "../../../services/notificationsAPI";
import Label from "../../../components/ui/labels/Label";
import { SelectContainer } from "../../../components/ui/layout/SelectContainer";
import Select from "react-select";

export const NotificationTable = ({ selectedJob, setOpenPoiOnMap, setActiveComponent }) => {
    const { hideLoading, showLoading } = useLoading()
    const { showError } = useError();
    const [ConfirmDialog, requestConfirm] = useConfirmDialog();
    const { timeoutSuccess } = useSuccess();
    const { user, setStoredJob } = useUser();
    const { runNameOptions, fetchRuns } = useRunOptions();
    const { fetchPOIGroupsByUser, poiGroupOptions } = usePoiGroupService()


    const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
    const [poiWithNotifData, setPoiWithNotifData] = useState([]);
    const [selectedRuns, setSelectedRuns] = useState([]);
    const [selectedPois, setSelectedPois] = useState([]); // Stores composite keys: "runId-poiId"
    const [selectedGroup, setSelectedGroup] = useState()

    useEffect(() => {
        if (selectedJob) {
            fetchRuns(selectedJob.value);
            fetchPOIGroupsByUser(selectedJob.value, true);
        }

    }, [selectedJob]); //eslint-disable-line

    useEffect(() => {
        if (selectedGroup && selectedRuns.length > 0) {
            fetchPOIData()
        }
    }, [selectedGroup, selectedRuns]); //eslint-disable-line  

    const fetchPOIData = async () => {
        showLoading('Getting POI Data');

        // Filter out "All Runs" option and get actual run IDs
        const runIds = selectedRuns
            .filter(run => run.value !== "all")
            .map(run => run.value);

        const poiWithNotifResponse = await getPOIWithNotificationByRunAPI(runIds, selectedJob.value, user.UserID, selectedGroup.value);

        if (!poiWithNotifResponse.success) {
            hideLoading();
            if (poiWithNotifResponse.statusCode === 404) {
                showError(`There are no POIs for the selected runs`)
            } else {
                showError(`Error Getting POIs - ${poiWithNotifResponse.message}`);
            }
        } else {
            hideLoading();
            // Group POIs by run for display with separators
            const groupedData = groupPOIsByRun(poiWithNotifResponse.data, selectedRuns);
            setPoiWithNotifData(groupedData);
            // Create composite keys for each POI-run combination
            let initialSelectedPois = poiWithNotifResponse.data.map((poi) => (
                `${poi.RunID}-${poi.POIID}`
            ));
            setSelectedPois(initialSelectedPois);
        }
    }

    const groupPOIsByRun = (pois, runs) => {
        // Create a map of run value to run label
        const runLabelMap = {};
        runs.forEach(run => {
            if (run.value !== "all") {
                runLabelMap[run.value] = run.label;
            }
        });

        // Group POIs by their RunID
        const grouped = {};
        pois.forEach(poi => {
            const runId = poi.RunID;
            if (!grouped[runId]) {
                grouped[runId] = {
                    runId: runId,
                    runName: runLabelMap[runId] || `Run ${runId}`,
                    pois: []
                };
            }
            grouped[runId].pois.push(poi);
        });

        // Convert to array format for rendering
        return Object.values(grouped);
    }

    const handleSelectedPoiChange = (checkboxValue, runId, poiId) => {
        const compositeKey = `${runId}-${poiId}`;
        let updatedSelectedPois = [...selectedPois];
        if (checkboxValue.checked) {
            updatedSelectedPois.push(compositeKey);
        } else {
            updatedSelectedPois = updatedSelectedPois.filter(key => key !== compositeKey);
        }

        setSelectedPois(updatedSelectedPois);
    }

    const handleSelectDeselectAll = (checkboxValue) => {
        let updatedSelectedPois
        if (checkboxValue.checked) {
            // Flatten all POIs from all run groups using composite keys
            updatedSelectedPois = poiWithNotifData.flatMap(runGroup =>
                runGroup.pois.map(poi => `${runGroup.runId}-${poi.POIID}`)
            )
        } else {
            updatedSelectedPois = []
        }

        setSelectedPois(updatedSelectedPois);
    }

    // Helper to get total POI count across all run groups
    const getTotalPoiCount = () => {
        return poiWithNotifData.reduce((total, runGroup) => total + runGroup.pois.length, 0);
    }

    const handleSetNotificationsClick = async () => {
        const isConfirmed = await requestConfirm('This action will overwrite any Notifications for any selected POI - Continue?')
        if (!isConfirmed) return;

        setOpenNotificationDialog(true);
    }

    const handleRemoveNotificationClick = async () => {
        const isConfirmed = await requestConfirm('This will Remove Notifications for all Selected POIs - Are you sure?')

        if (!isConfirmed) return;

        showLoading('Removing Notifications...');

        // Parse composite keys ("runId-poiId") into structured array
        const poiSelections = selectedPois.map(compositeKey => {
            const [runId, poiId] = compositeKey.split('-').map(Number);
            return { runId, poiId };
        });

        let toRemoveNotifications = {
            poiSelections: poiSelections, // Array of {runId, poiId} for granular removal
            userID: user.UserID
        }
        const response = await deletePOINotificationAPI(toRemoveNotifications);

        if (response.success) {
            hideLoading();
            fetchPOIData();
            timeoutSuccess(`Notifications Removed`)

            //update poi notification count in storedJob ( so we can display indicator if not all runs have a notification)
            setStoredJob(prev => {
                if (!prev || !prev.notificationCount) return prev;

                const updatedRuns = { ...prev.notificationCount };

                // Decrement POINotificationCount for each run affected
                poiSelections.forEach(({ runId }) => {
                    if (updatedRuns[runId]) {
                        
                        updatedRuns[runId] = {
                            ...updatedRuns[runId],
                            POINotificationCount: 0
                        };
                    }
                });
                console.log(updatedRuns)
                return {
                    ...prev,
                    notificationCount: updatedRuns
                };
            });
        } else {
            hideLoading();
            showError('Error Removing Notifications for POIs');
        }
    }

    const handleOpenPoiOnMap = (poiId) => {
        setOpenPoiOnMap({ poiId, jobId: selectedJob.value })
        setActiveComponent('Map')
    }

    const getSendToDetails = (poi, runId) => {
        // Filter notifications to only show those for the current run
        const runNotifications = poi.notifications.filter(notif => notif.RunID === runId);

        if (!runNotifications.length) {
            return <p></p>
        }

        let notif = runNotifications[0];

        return (
            <div className="gap-1 gap-x-1 flex flex-col">
                {(notif.AltEmail && notif.IsEmail) ? 
                    <div className="bg-blue-300 rounded-md p-1 text-center text-xs">
                        <p>{notif.AltEmail}</p> 
                    </div>
                : (!notif.AltEmail && notif.IsEmail) ? 
                    <div className="bg-blue-300 rounded-md p-1 text-center text-xs">
                        <p>{user.Email}</p> 
                    </div>
                : <span className="hidden"></span>}
                
                {(notif.AltPhone && notif.IsPhone) ? 
                    <div className="bg-blue-300 rounded-md p-1 text-center text-xs">
                        <p>{notif.AltPhone}</p> 
                    </div>
                : (!notif.AltPhone && notif.IsPhone) ? 
                    <div className="bg-blue-300 rounded-md p-1 text-center text-xs">
                        <p>{user.Phone}</p> 
                    </div>
                : <span className="hidden"></span>}
            </div>
        )
    }

    if (!selectedJob) {
        return <div className='martrak-page-padding-left-right'>Please select a job</div>
    }

    return (
        <div className='martrak-page-padding-left-right'>
            <ModalWrapper
                isOpen={openNotificationDialog}
                onClose={() => setOpenNotificationDialog(false)}
                size="medium"
                title='Create Notification for POI'
            >
                <NotificationDialog
                    selectedJob={selectedJob}
                    selectedPois={selectedPois}
                    selectedRuns={selectedRuns}
                    setOpenNotificationDialog={setOpenNotificationDialog}
                    fetchPOIData={fetchPOIData}
                    selectedGroup={selectedGroup}
                />
            </ModalWrapper>
            <div>
                <RunSelect
                    className="mb-10 z-[11]"
                    runNameOptions={runNameOptions}
                    selectedRuns={selectedRuns}
                    setSelectedRuns={setSelectedRuns}
                />
            </div>
            {selectedRuns.length > 0 &&
                <div>
                    <Label>Select a POI Group</Label>
                    <SelectContainer>
                        <Select
                            value={selectedGroup}
                            options={poiGroupOptions}
                            onChange={(option) => setSelectedGroup(option)}
                            name="poi-group-dropdown"
                            menuPosition='fixed'
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </SelectContainer>
                </div>
            }
            
            {poiWithNotifData.length > 0 && getTotalPoiCount() > 0 &&

                <>
                    <div className="flex justify-around mb-5">
                        <Button
                            onClick={handleSetNotificationsClick}
                        >
                            Set Notifications
                        </Button>
                        {/* <Button>Select All</Button> */}
                        <Button
                            variant="danger"
                            onClick={handleRemoveNotificationClick}
                        >
                            Remove Notifications
                        </Button>
                    </div>

                    <div className="flex">
                        <Checkbox
                            name='select-all-check'
                            initiallyChecked={true}
                            checked={selectedPois.length === getTotalPoiCount()}
                            onChange={(checkboxValue) => handleSelectDeselectAll(checkboxValue)}
                         />

                        <Label htmlFor='select-all-check' className="ml-2">Select All</Label>

                    </div>
                </>

            }
            <div className="overflow-x-auto overflow-y-auto h-96 max-w-full">



                <table className="min-w-full border-collapse table-auto bg-white">
                    <thead className="bg-gray-50 sticky-header">
                        <tr>
                            {/* <th className="px-4 py-2 sticky-left sticky-left-header min-w-10"></th> */}
                            <th className="px-4 py-2 sticky-left sticky-left-header min-w-40">Name</th>
                            <th className="px-4 py-2 min-w-40">Time Before (mins)</th>
                            <th className="px-4 py-2 min-w-40">Send To</th>
                            <th className="px-4 py-2 min-w-40">POI Description</th>
                            <th className="px-4 py-2 min-w-40">View On Map</th>
                        </tr>
                    </thead>
                    <tbody>
                        {poiWithNotifData.length > 0 && poiWithNotifData.map((runGroup) => (
                            <Fragment key={`run-group-${runGroup.runId}`}>
                                {/* Run Separator Row */}
                                <tr className="bg-blue-100">
                                    <td colSpan={5} className="px-4 py-3 text-center font-bold text-blue-800 text-lg">
                                        {runGroup.runName}
                                    </td>
                                </tr>
                                {/* POI Rows for this run */}
                                {runGroup.pois.map((poi, index) => {
                                    const rowBgColor = index % 2 === 0 ? 'bg-gray-100' : 'bg-white';
                                    return (
                                        <tr key={poi.POIID}
                                            className={`hover:bg-slate-200 ${rowBgColor}`}
                                        >
                                            <td className={`sticky-left ${rowBgColor}`} style={{ backgroundColor: index % 2 === 0 ? '#f3f4f6' : '#ffffff' }}>
                                                <div className="grid grid-cols-3">
                                                    <div className="mx-auto">
                                                        <Checkbox
                                                            name='notif-check'
                                                            checked={selectedPois.includes(`${runGroup.runId}-${poi.POIID}`)}
                                                            onChange={(checkboxValue) => handleSelectedPoiChange(checkboxValue, runGroup.runId, poi.POIID)}
                                                        />
                                                    </div>
                                                    <div className="col-span-2">
                                                        {poi.Linked_POI_Name ? poi.Linked_POI_Name : poi.POI_Name}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="grid grid-cols-3">
                                                {(() => {
                                                    // Filter notifications to only show those for the current run
                                                    const runNotifications = poi.notifications.filter(
                                                        notif => notif.RunID === runGroup.runId
                                                    );
                                                    return runNotifications.length ? runNotifications.map((notif, notifIndex) => (
                                                        <div
                                                            key={notifIndex}
                                                            className="px-1 mx-auto border border-black rounded-sm text-xs bg-green-100"
                                                        >
                                                            {notif.Interval}(m)
                                                        </div>
                                                    ))
                                                    :
                                                    <div className="col-span-3 pt-1">
                                                        <p className="text-xs bg-red-500 text-white rounded-md text-center mx-auto py-auto">No Notifications Set</p>
                                                    </div>
                                                })()}
                                            </td>
                                            <td>
                                                {getSendToDetails(poi, runGroup.runId)}
                                            </td>
                                            <td>
                                                {
                                                    poi.Linked_POI_Description ? poi.Linked_POI_Description
                                                    : poi.Description ? poi.Description
                                                    : ""
                                                }
                                            </td>
                                            <td>
                                                <div className='px-4 py-2 flex justify-center items-center'>
                                                    <img
                                                        src={MapIcon}
                                                        alt="Google Map"
                                                        className='w-6 hover:cursor-pointer'
                                                        onClick={() => handleOpenPoiOnMap(poi.POIID)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <ConfirmDialog />
        </div>
    )
}